import { useState } from "react";
import { COMPANY } from "../data/content";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    navn: "",
    telefon: "",
    email: "",
    beskrivelse: "",
    website_url: "", // Mandatory honeypot field
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Basic validation
    if (!formData.navn.trim() || !formData.telefon.trim() || !formData.email.trim() || !formData.beskrivelse.trim()) {
      setError("Udfyld venligst alle påkrævede felter.");
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          navn: formData.navn,
          telefon: formData.telefon,
          email: formData.email,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url, // Honeypot field
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          navn: "",
          telefon: "",
          email: "",
          beskrivelse: "",
          website_url: "",
        });
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.message || "Der opstod en fejl ved afsendelsen. Prøv venligst igen eller kontakt os pr. telefon.");
      }
    } catch (err) {
      console.error("Webform submit error:", err);
      setError("Der opstod en netværksfejl. Kontrollér din forbindelse og prøv igen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center space-y-4">
        <div className="w-16 h-16 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto text-2xl">
          <i className="fa-solid fa-circle-check"></i>
        </div>
        <h3 className="text-2xl font-bold text-forest-900">Mange tak for din henvendelse!</h3>
        <p className="text-charcoal-700 max-w-md mx-auto leading-relaxed">
          Jeg har modtaget din besked og vender tilbage til dig hurtigst muligt. Har du hastende spørgsmål, er du velkommen til at ringe direkte.
        </p>
        <div className="pt-2">
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-forest-900 text-white font-semibold text-sm hover:bg-forest-800 transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-phone text-emerald-400"></i>
            <span className="whitespace-nowrap">Ring (+45) 20 64 99 29</span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-8 border border-sand-300 shadow-sm space-y-5">
      {/* Honeypot anti-spam field (MUST BE ABSOLUTELY HIDDEN VIA SR-ONLY + ABSOLUTE) */}
      <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="website_url">Website URL (leave empty)</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {/* Navn */}
        <div>
          <label htmlFor="navn" className="block text-xs font-bold uppercase tracking-wider text-charcoal-800 mb-1.5">
            Dit Navn <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            id="navn"
            name="navn"
            required
            value={formData.navn}
            onChange={handleChange}
            placeholder="f.eks. Mette Hansen"
            className="w-full px-4 py-3 rounded-xl border border-sand-300 bg-sand-50/50 text-charcoal-900 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-forest-700 focus:border-transparent transition-all"
          />
        </div>

        {/* Telefonnummer */}
        <div>
          <label htmlFor="telefon" className="block text-xs font-bold uppercase tracking-wider text-charcoal-800 mb-1.5">
            Telefonnummer <span className="text-red-600">*</span>
          </label>
          <input
            type="tel"
            id="telefon"
            name="telefon"
            required
            value={formData.telefon}
            onChange={handleChange}
            placeholder="f.eks. 20 64 99 29"
            className="w-full px-4 py-3 rounded-xl border border-sand-300 bg-sand-50/50 text-charcoal-900 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-forest-700 focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-charcoal-800 mb-1.5">
          E-mailadresse <span className="text-red-600">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          value={formData.email}
          onChange={handleChange}
          placeholder="f.eks. din.email@eksempel.dk"
          className="w-full px-4 py-3 rounded-xl border border-sand-300 bg-sand-50/50 text-charcoal-900 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-forest-700 focus:border-transparent transition-all"
        />
      </div>

      {/* Beskrivelse */}
      <div>
        <label htmlFor="beskrivelse" className="block text-xs font-bold uppercase tracking-wider text-charcoal-800 mb-1.5">
          Beskrivelse / Hvor i Jylland står hesten? <span className="text-red-600">*</span>
        </label>
        <textarea
          id="beskrivelse"
          name="beskrivelse"
          rows={5}
          required
          value={formData.beskrivelse}
          onChange={handleChange}
          placeholder="Skriv gerne lidt om din hest, antal heste, adresse/by i Jylland samt hvornår det passer dig..."
          className="w-full px-4 py-3 rounded-xl border border-sand-300 bg-sand-50/50 text-charcoal-900 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-forest-700 focus:border-transparent transition-all resize-y"
        ></textarea>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-3.5 bg-red-50 border border-red-200 text-red-800 rounded-xl text-xs flex items-center gap-2">
          <i className="fa-solid fa-circle-exclamation text-red-600 text-base shrink-0"></i>
          <span>{error}</span>
        </div>
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3.5 px-6 rounded-xl bg-forest-900 hover:bg-forest-800 text-white font-bold text-base transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-600 cursor-pointer flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {submitting ? (
          <>
            <i className="fa-solid fa-spinner fa-spin"></i>
            <span>Sender din besked...</span>
          </>
        ) : (
          <>
            <i className="fa-solid fa-paper-plane text-emerald-400"></i>
            <span>Send besked</span>
          </>
        )}
      </button>

      <p className="text-[11px] text-center text-charcoal-600">
        Dine oplysninger behandles fortroligt og bruges udelukkende til at besvare din henvendelse.
      </p>
    </form>
  );
}
